import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Check, AlertTriangle, HelpCircle, TrendingDown, Lightbulb, Zap, Brain, Shield, Users, Briefcase, Rocket, GraduationCap, Building2, Mic2, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const stagger = (i) => ({ ...fade, transition: { duration: 0.6, delay: i * 0.1 } });

// ── 1. HERO ──

function HeroSection() {
  return (
    <section className="flex flex-col" data-testid="aiw-hero">
      {/* Header banner */}
      <motion.div
        className="w-full max-w-[1100px] mx-auto px-6 md:px-12 pt-24 pb-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
      >
        <img
          src="/assets/ai-whisperer-header.jpg"
          alt="The AI Whisperer — R. Dale Brunet — Author, Technologist, Speaker"
          className="w-full max-w-2xl rounded-xl"
          data-testid="aiw-header-banner"
        />
      </motion.div>

      <div className="max-w-[1100px] mx-auto px-6 md:px-12 pb-10 w-full">
        <div className="flex flex-col md:flex-row md:items-center md:gap-12">
          <div className="flex-1">
            <motion.span
              className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            >
              A Different Way to Think About AI
            </motion.span>

            <motion.h1
              className="text-5xl md:text-7xl font-bold text-white mb-3 leading-[1.1]"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              data-testid="aiw-headline"
            >
              The AI Whisperer
            </motion.h1>

            <motion.h2
              className="text-xl md:text-2xl text-slate-300 font-medium mb-6"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            >
              Most people are asking the wrong question.
            </motion.h2>

            <motion.div
              className="space-y-3 text-base md:text-lg text-slate-400 leading-relaxed mb-4 max-w-2xl"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            >
              <p>Artificial intelligence is not just another tool. It is a pattern recognition system that reflects how you think, how you ask, and how you decide.</p>
              <p>Most people approach it like software.</p>
              <p className="text-slate-300">That is why they get average results.</p>
            </motion.div>

            <motion.p
              className="text-base md:text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
              data-testid="aiw-keynote-context"
            >
              This is the foundation of <span className="text-white font-semibold">The AI Whisperer keynote</span>—a session designed to help audiences move from confusion to clarity in real time.
            </motion.p>

            {/* Mobile book image */}
            <motion.div
              className="flex md:hidden justify-center mb-6"
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35, duration: 0.6 }}
            >
              <img
                src="/assets/ai-whisperer-book-3d.png"
                alt="The AI Whisperer - Book by R. Dale Brunet"
                className="w-[130px] drop-shadow-2xl"
                data-testid="aiw-hero-book-mobile"
              />
            </motion.div>

            <motion.div
              className="flex flex-wrap gap-3"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            >
              <Button
                asChild
                size="lg"
                className="bg-primary text-white hover:text-white hover:bg-primary/80 shadow-[0_4px_0_0_rgba(168,130,20,1)] hover:shadow-[0_2px_0_0_rgba(168,130,20,1)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all duration-100"
                data-testid="aiw-hero-book-keynote"
              >
                <Link to="/contact">Book This Keynote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800" data-testid="aiw-hero-paperback">
                <a href="https://books.by/use-what-you-got-publishing/the-ai-whisperer" target="_blank" rel="noopener noreferrer">Order the Paperback</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800" data-testid="aiw-hero-substack">
                <a href="https://substack.com/@youraiwhisperer" target="_blank" rel="noopener noreferrer">More on Substack</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800" data-testid="aiw-hero-newaudia">
                <a href="https://www.newaudia.com/public/library" target="_blank" rel="noopener noreferrer">Listen on NewAudia.com</a>
              </Button>
            </motion.div>
          </div>

          {/* Desktop 3D Book cover */}
          <motion.div
            className="hidden md:flex items-center justify-center flex-shrink-0"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <img
              src="/assets/ai-whisperer-book-3d.png"
              alt="The AI Whisperer - Book by R. Dale Brunet"
              className="w-48 lg:w-60 drop-shadow-2xl"
              data-testid="aiw-hero-book"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ── 2. THE PROBLEM ──

function ProblemSection() {
  return (
    <section id="the-problem" className="py-10 md:py-16 relative overflow-hidden" data-testid="aiw-problem">
      {/* Decorative background */}
      <div className="absolute right-0 top-0 w-96 h-96 bg-red-500/3 rounded-full blur-[120px] pointer-events-none" />
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <motion.h2 {...fade} className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Problem Isn't AI
            </motion.h2>
            <motion.div {...fade} className="space-y-4 text-base md:text-lg text-slate-400 leading-relaxed">
              <p className="text-slate-300">The problem is how people are approaching it.</p>
              <p>They are asking:<br /><span className="text-slate-300 italic">"What can this do for me?"</span></p>
              <p>They are looking for shortcuts, automation, and easy answers.</p>
              <p>What they get back is surface-level output, inconsistent results, and a growing sense that something is missing.</p>
              <p className="text-white font-medium">Because it is.</p>
            </motion.div>
          </div>
          <motion.div className="lg:col-span-2 hidden lg:block" {...fade}>
            <img
              src="https://images.unsplash.com/photo-1674027444485-cec3da58eef4?w=600&q=80"
              alt="Abstract AI neural network"
              className="rounded-2xl opacity-60 w-full aspect-square object-cover"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ── 3. AUDIENCE HOOK ──

const audienceGroups = [
  { icon: AlertTriangle, label: 'Overwhelmed', desc: 'They feel the pressure but don\'t know where to start.' },
  { icon: HelpCircle, label: 'Skeptical', desc: 'They\'ve heard the hype and aren\'t buying it.' },
  { icon: TrendingDown, label: 'Underusing it', desc: 'They\'re using AI — but not effectively.' },
];

function AudienceHookSection() {
  return (
    <section id="audience" className="py-10 md:py-16 bg-slate-900/50" data-testid="aiw-audience">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <motion.h2 {...fade} className="text-3xl md:text-4xl font-bold text-white mb-4">
          Why This Matters to Your Audience
        </motion.h2>
        <motion.p {...fade} className="text-base md:text-lg text-slate-400 mb-6">
          Most audiences today fall into one of three groups:
        </motion.p>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {audienceGroups.map((g, i) => (
            <motion.div
              key={g.label}
              className="border border-border/30 rounded-xl p-5 bg-card/60 text-center"
              {...stagger(i)}
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <g.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-white font-bold mb-1">{g.label}</h3>
              <p className="text-sm text-slate-400">{g.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div {...fade} className="space-y-3 text-base md:text-lg text-slate-400 leading-relaxed">
          <p>This keynote meets them where they are and moves them forward.</p>
          <p>Not with theory.</p>
          <p className="text-white font-medium">With clarity they can use immediately.</p>
        </motion.div>
      </div>
    </section>
  );
}

// ── 4. THE SHIFT ──

function ShiftSection() {
  return (
    <section id="the-shift" className="py-12 md:py-[72px] relative overflow-hidden" data-testid="aiw-shift">
      {/* Decorative glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[500px] bg-primary/5 rounded-full blur-[150px]" />
      </div>
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative">
        <motion.div {...fade} className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">
          <Zap className="w-4 h-4 text-primary" />
          <span className="text-sm text-primary font-semibold">The Key Insight</span>
        </motion.div>
        <motion.h2 {...fade} className="text-3xl md:text-5xl font-bold text-white mb-6">
          The Shift
        </motion.h2>
        <motion.div {...fade} className="space-y-4 text-lg md:text-xl leading-relaxed">
          <p className="text-slate-400">
            The real question is not:<br />
            <span className="text-slate-300 italic">"What can AI do for me?"</span>
          </p>
          <div className="py-4">
            <div className="inline-block bg-primary/10 border border-primary/30 rounded-2xl px-8 py-5">
              <p className="text-2xl md:text-3xl font-bold text-primary">"What can I do with AI?"</p>
            </div>
          </div>
          <p className="text-white font-semibold text-xl md:text-2xl pt-2">That shift changes everything.</p>
          <div className="space-y-1 text-slate-300 pt-1">
            <p>It moves you from passive user to active participant.</p>
            <p>From consumer to creator.</p>
            <p>From reacting to directing.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── 5. TRANSFORMATION ──

const outcomes = [
  { icon: Lightbulb, text: 'Stop asking surface-level questions' },
  { icon: Brain, text: 'Start thinking more clearly about how to use AI' },
  { icon: Shield, text: 'Understand how to guide outputs instead of reacting to them' },
  { icon: Rocket, text: 'Walk away with a practical way to engage immediately' },
];

function TransformationSection() {
  return (
    <section id="transformation" className="py-10 md:py-16 bg-slate-900/50" data-testid="aiw-transformation">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <motion.h2 {...fade} className="text-3xl md:text-4xl font-bold text-white mb-6">
          What Changes in the Room
        </motion.h2>
        <motion.p {...fade} className="text-base md:text-lg text-slate-400 mb-6">
          By the end of this session, your audience will:
        </motion.p>

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          {outcomes.map((item, i) => (
            <motion.div
              key={item.text}
              className="flex items-start gap-4 border border-border/30 rounded-xl p-5 bg-card/60"
              {...stagger(i)}
            >
              <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-5 h-5 text-primary" />
              </div>
              <span className="text-slate-300 leading-relaxed">{item.text}</span>
            </motion.div>
          ))}
        </div>

        <motion.div {...fade} className="text-base md:text-lg">
          <p className="text-white font-medium">This is not a passive talk.</p>
          <p className="text-slate-300">It's a shift in how people think.</p>
        </motion.div>
      </div>
    </section>
  );
}

// ── 6. ELSIE — SPECIAL FEATURE ──

const elsieFeatures = [
  'Conversational AI beyond simple search',
  'Context-aware interaction',
  'Reflection instead of mere retrieval',
  'A glimpse into the future of human-AI collaboration',
];

function ElsieSection() {
  return (
    <section id="elsie" className="py-10 md:py-16" data-testid="aiw-elsie">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-6">
          <motion.img
            src="https://customer-assets.emergentagent.com/job_6ab5280f-8574-4593-a0f4-4545bb64e3a0/artifacts/ldd278sl_LCAI_POC_APP_IMAGE_1.jpg"
            alt="Elsie - LifeCapsule AI Digital Legacy Preservation Assistant"
            className="w-40 md:w-48 rounded-xl border border-border/30 shadow-lg flex-shrink-0"
            {...fade}
            data-testid="aiw-elsie-image"
          />
          <div>
            <motion.span {...fade} className="text-sm text-primary font-medium uppercase tracking-widest mb-2 block">
              Special Feature
            </motion.span>
            <motion.h2 {...fade} className="text-3xl md:text-4xl font-bold text-white">
              Meet Elsie — A Different Kind of AI
            </motion.h2>
          </div>
        </div>
        <motion.div {...fade} className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3 space-y-4 text-base md:text-lg text-slate-400 leading-relaxed">
            <p>
              This presentation can also include <span className="text-white font-semibold">Elsie</span>, the LifeCapsule AI avatar, as a featured part of the experience.
            </p>
            <p>
              Elsie helps audiences see that AI can become something far more meaningful than a glorified search engine. Rather than simply retrieving information, Elsie represents a more contextual, reflective, and conversational form of artificial intelligence — one designed to listen, engage, and respond in ways that feel more human-centered.
            </p>
            <p className="text-sm text-slate-500">
              Depending on the format, Elsie may appear through a short introduction video and/or as a live interactive presentation element.
            </p>
          </div>
          <div className="lg:col-span-2">
            <div className="bg-slate-800/50 border border-border/30 rounded-xl p-6 space-y-3">
              {elsieFeatures.map((item, i) => (
                <motion.div key={item} className="flex items-start gap-3" {...stagger(i)}>
                  <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── 7. WHAT AN AI WHISPERER DOES ──

function WhatItIsSection() {
  return (
    <section id="what-it-is" className="py-10 md:py-16 relative overflow-hidden" data-testid="aiw-what-it-is">
      <div className="absolute left-0 bottom-0 w-80 h-80 bg-primary/3 rounded-full blur-[100px] pointer-events-none" />
      <div className="max-w-3xl mx-auto px-6 md:px-12 relative">
        <motion.h2 {...fade} className="text-3xl md:text-4xl font-bold text-white mb-4">
          What an AI Whisperer Does
        </motion.h2>
        <motion.div {...fade} className="space-y-4 text-base md:text-lg text-slate-400 leading-relaxed">
          <p>An AI Whisperer does not treat AI like a machine that produces answers.</p>
          <p className="text-slate-300">They treat it like a system that responds to thinking.</p>
          <p>They understand that better inputs create better outputs.</p>
          <p>They recognize patterns.</p>
          <p>They refine their approach.</p>
          <p>They ask better questions.</p>
          <p>And over time, they develop an ability that most people never do:</p>
          <motion.p
            className="text-white font-medium text-lg md:text-xl bg-primary/5 border-l-4 border-primary pl-5 py-3 rounded-r-lg"
            {...fade}
          >
            They know how to get what they actually want out of the interaction.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// ── 8. WHAT THIS MEANS FOR YOU ──

const cards = [
  { icon: Lightbulb, title: 'Less Frustration', body: 'You stop guessing and start understanding why you are getting the results you are getting.' },
  { icon: Zap, title: 'Better Output', body: 'You learn how to shape responses instead of accepting whatever shows up.' },
  { icon: Brain, title: 'Faster Thinking', body: 'You begin to use AI as a thinking partner, not just a tool.' },
  { icon: Shield, title: 'More Control', body: 'You realize you are not being replaced. You are being amplified.' },
];

function MeansSection() {
  return (
    <section id="what-this-means" className="py-10 md:py-16 bg-slate-900/50" data-testid="aiw-means">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <motion.h2 {...fade} className="text-3xl md:text-4xl font-bold text-white mb-6">
          What This Means for You
        </motion.h2>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
          {cards.map((c, i) => (
            <motion.div
              key={c.title}
              className="border border-border/30 rounded-xl p-6 bg-card"
              {...stagger(i)}
            >
              <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center mb-3">
                <c.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{c.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── 9. SOCIAL PROOF ──

function SocialProofSection() {
  return (
    <section id="social-proof" className="py-10 md:py-16 relative overflow-hidden" data-testid="aiw-social-proof">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[400px] h-[400px] bg-primary/4 rounded-full blur-[120px]" />
      </div>
      <div className="max-w-3xl mx-auto px-6 md:px-12 relative text-center">
        <motion.h2 {...fade} className="text-3xl md:text-4xl font-bold text-white mb-8">
          What People Realize
        </motion.h2>
        <motion.div {...fade}>
          <div className="relative inline-block">
            <Quote className="w-12 h-12 text-primary/20 absolute -top-2 -left-6" />
            <p className="text-2xl md:text-3xl text-slate-200 italic leading-snug font-light pl-6">
              I didn't realize I could think about AI this way.
            </p>
            <Quote className="w-12 h-12 text-primary/20 absolute -bottom-2 -right-6 rotate-180" />
          </div>
          <div className="w-16 h-1 bg-primary/40 rounded-full mx-auto my-6" />
          <p className="text-base md:text-lg text-slate-400">That is the most common response.</p>
          <p className="text-white font-medium mt-2">Because once people see it, they can't unsee it.</p>
        </motion.div>
      </div>
    </section>
  );
}

// ── 10. REAL-WORLD CONTEXT ──

const domains = [
  { label: 'Business', icon: Briefcase },
  { label: 'Content creation', icon: Zap },
  { label: 'Writing and publishing', icon: Quote },
  { label: 'Problem solving', icon: Lightbulb },
  { label: 'Decision making', icon: Brain },
  { label: 'Personal organization', icon: Shield },
  { label: 'Learning and exploration', icon: GraduationCap },
];

function RealWorldSection() {
  return (
    <section id="real-world" className="py-10 md:py-16 bg-slate-900/50" data-testid="aiw-real-world">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <motion.h2 {...fade} className="text-3xl md:text-4xl font-bold text-white mb-4">
          Where This Applies
        </motion.h2>
        <motion.p {...fade} className="text-base md:text-lg text-slate-400 mb-6">
          This way of thinking applies across everything:
        </motion.p>
        <motion.div {...fade} className="flex flex-wrap gap-3 mb-5">
          {domains.map((d, i) => (
            <motion.span
              key={d.label}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 bg-slate-800/60 border border-border/20 rounded-lg"
              {...stagger(i)}
            >
              <d.icon className="w-4 h-4 text-primary" />
              {d.label}
            </motion.span>
          ))}
        </motion.div>
        <motion.p {...fade} className="text-base md:text-lg text-white font-medium">
          Anywhere AI shows up, your approach determines the outcome.
        </motion.p>
      </div>
    </section>
  );
}

// ── 11. POSITIONING STATEMENT ──

function PositioningSection() {
  return (
    <section id="positioning" className="py-10 md:py-16 relative overflow-hidden" data-testid="aiw-positioning">
      {/* Background image with heavy overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1770210217380-d78a69acdc77?w=1200&q=60"
          alt=""
          className="w-full h-full object-cover opacity-[0.07]"
        />
      </div>
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative">
        <motion.h2 {...fade} className="text-3xl md:text-5xl font-bold text-white mb-6">
          This Isn't About Technology
        </motion.h2>
        <motion.div {...fade} className="space-y-4 text-lg md:text-xl text-slate-400 leading-relaxed">
          <p className="text-slate-300 font-medium">This is about how you think.</p>
          <p>The AI Whisperer is not a role.</p>
          <p>It is not a title.</p>
          <p>It is a way of interacting with a system that is already shaping the world around you.</p>
          <p className="text-white font-semibold pt-1">And the people who understand that first will not just use AI.</p>
          <p className="text-primary font-bold text-xl md:text-2xl">They will direct it.</p>
        </motion.div>
      </div>
    </section>
  );
}

// ── 12. KEYNOTE POSITIONING ──

const audiences = [
  { icon: Building2, label: 'Business and leadership audiences' },
  { icon: Rocket, label: 'Entrepreneur and startup communities' },
  { icon: Brain, label: 'Conferences exploring AI and innovation' },
  { icon: Users, label: 'Organizations navigating change' },
];

function KeynoteSection() {
  return (
    <section id="keynote" className="py-10 md:py-16 bg-slate-900/50" data-testid="aiw-keynote">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3">
            <motion.h2 {...fade} className="text-3xl md:text-4xl font-bold text-white mb-4">
              The AI Whisperer Keynote
            </motion.h2>
            <motion.p {...fade} className="text-slate-300 mb-6">
              A high-impact, perspective-shifting session designed for:
            </motion.p>
            <div className="space-y-3">
              {audiences.map((a, i) => (
                <motion.div key={a.label} className="flex items-center gap-3" {...stagger(i)}>
                  <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <a.icon className="w-4.5 h-4.5 text-primary" />
                  </div>
                  <span className="text-slate-300">{a.label}</span>
                </motion.div>
              ))}
            </div>
            <motion.p {...fade} className="text-white font-medium mt-6">
              Delivered as a keynote, breakout session, or strategic discussion.
            </motion.p>
          </div>
          <motion.div className="lg:col-span-2 hidden lg:block" {...fade}>
            <img
              src="/assets/r-dale-brunet-stage.jpg"
              alt="R. Dale Brunet speaking on stage"
              className="rounded-2xl w-full aspect-[4/3] object-cover"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ── 13. CTA ──

function CTASection() {
  return (
    <section id="cta" className="pt-14 md:pt-14 pb-[72px]" data-testid="aiw-cta">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-center md:gap-12">
          {/* Medallion */}
          <motion.div
            className="flex justify-center md:justify-start flex-shrink-0 mb-8 md:mb-0"
            {...fade}
          >
            <img
              src="/assets/ai-whisperer-medallion.png"
              alt="R. Dale Brunet - The AI Whisperer"
              className="w-36 md:w-48"
              data-testid="aiw-medallion"
            />
          </motion.div>

          {/* CTA content */}
          <div className="flex-1 text-center md:text-left">
            <motion.h2 {...fade} className="text-3xl md:text-4xl font-bold text-white mb-4">
              Bring The AI Whisperer to Your Audience
            </motion.h2>
            <motion.p {...fade} className="text-base md:text-lg text-slate-400 mb-8">
              If your audience is asking questions about AI, they are already feeling the pressure of change. This keynote gives them a way to think clearly, act confidently, and engage with what's coming—without fear or confusion.
            </motion.p>
            <motion.div {...fade} className="flex flex-wrap justify-center md:justify-start gap-4">
              <Button asChild size="lg" className="bg-primary text-white hover:text-white hover:bg-primary/80 shadow-[0_4px_0_0_rgba(168,130,20,1)] hover:shadow-[0_2px_0_0_rgba(168,130,20,1)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all duration-100" data-testid="aiw-cta-book">
                <Link to="/contact">Book The AI Whisperer Keynote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800" data-testid="aiw-cta-speaking">
                <Link to="/speaking">View Speaking Details <ChevronRight className="w-4 h-4 ml-1" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800" data-testid="aiw-cta-substack">
                <a href="https://substack.com/@youraiwhisperer" target="_blank" rel="noopener noreferrer">Read on Substack <ChevronRight className="w-4 h-4 ml-1" /></a>
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── MAIN ──

export default function AIWhispererPage() {
  useEffect(() => {
    document.title = 'The AI Whisperer Keynote | R. Dale Brunet';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'The AI Whisperer keynote helps audiences move from confusion to clarity in a world shaped by artificial intelligence. Book R. Dale Brunet for your next event.');
    return () => { document.title = 'R. Dale Brunet | Technology Architect, AI Strategist, Founder, Author'; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="pt-20" data-testid="aiw-page">
      <HeroSection />
      <ProblemSection />
      <AudienceHookSection />
      <ShiftSection />
      <TransformationSection />
      <ElsieSection />
      <WhatItIsSection />
      <MeansSection />
      <SocialProofSection />
      <RealWorldSection />
      <PositioningSection />
      <KeynoteSection />
      <CTASection />
    </main>
  );
}
